import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import {
  ArrowLeft, ShoppingBag, Loader2, Plus, Minus, Truck,
  Sparkles, Check, AlertCircle, Wine,
} from "lucide-react";

/**
 * ShopProduct — product detail with variant selection + Add to cart.
 * Uses GET /api/shop/products/:store_product_id.
 */
export default function ShopProduct() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem, totals } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const run = async () => {
      try {
        const r = await api.get(`/shop/products/${id}`);
        if (!active) return;
        setProduct(r.data);
        const firstVariant = (r.data?.variants || [])[0];
        if (firstVariant) setSelectedId(firstVariant.id);
      } catch (e) {
        if (!active) return;
        setError(e?.response?.data?.detail || "Produit indisponible");
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [id]);

  const selected = useMemo(
    () => (product?.variants || []).find((v) => v.id === selectedId),
    [product, selectedId]
  );

  // Group variant options for cleaner UI
  const grouped = useMemo(() => {
    if (!product?.variants) return {};
    const out = {};
    product.variants.forEach((v) => {
      (v.options || []).forEach((o) => {
        if (!out[o.name]) out[o.name] = new Set();
        out[o.name].add(o.value);
      });
    });
    return Object.fromEntries(Object.entries(out).map(([k, set]) => [k, [...set]]));
  }, [product]);

  // Selected option values, e.g. {Couleur: "Vintage", Taille: "30 x 40 cm"}
  const selectedOpts = useMemo(() => {
    const map = {};
    (selected?.options || []).forEach((o) => { map[o.name] = o.value; });
    return map;
  }, [selected]);

  const handleOptionChange = (name, value) => {
    // Find a variant matching all current options with the changed one
    const candidates = (product?.variants || []).filter((v) => {
      const map = {};
      (v.options || []).forEach((o) => { map[o.name] = o.value; });
      const desired = { ...selectedOpts, [name]: value };
      return Object.entries(desired).every(([k, val]) => map[k] === val);
    });
    if (candidates[0]) setSelectedId(candidates[0].id);
    else {
      // No exact match — pick the first variant with the new option value alone
      const fallback = (product?.variants || []).find((v) =>
        (v.options || []).some((o) => o.name === name && o.value === value)
      );
      if (fallback) setSelectedId(fallback.id);
    }
  };

  const onAdd = async () => {
    if (!selected) return;
    setAdding(true);
    addItem({
      variant_id: selected.id,
      product_id: product.id,
      store_id: product.store_id,
      product_name: product.name,
      image: selected.preview || product.image,
      variant_title: selected.title,
      unit_price_eur: selected.price_eur,
      currency: selected.currency,
      quantity: qty,
    });
    toast.success("Ajouté au panier", { description: product.name });
    setTimeout(() => setAdding(false), 400);
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-16 flex items-center gap-2 text-[#8B95A1]" data-testid="shop-product-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
      </div>
    );
  }
  if (error || !product) {
    return (
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-16" data-testid="shop-product-error">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#8B95A1] hover:text-[#E5276A]"><ArrowLeft className="w-4 h-4" /> Retour à la boutique</Link>
        <div className="mt-6 p-6 rounded-2xl bg-[#FCE4ED] border border-[#E5276A]/20 text-[#1F3A5F]">
          <AlertCircle className="w-5 h-5 text-[#E5276A] mb-2" />
          <p className="font-bold">Produit indisponible</p>
          <p className="text-sm text-[#4A5568] mt-1">{error || "Ce produit n'existe plus ou a été retiré du catalogue."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="shop-product-page">
      <div className="flex items-center justify-between mb-6">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#8B95A1] hover:text-[#E5276A]"
          data-testid="shop-back-to-list">
          <ArrowLeft className="w-4 h-4" /> Retour à la boutique
        </Link>
        <Link to="/shop/cart" className="relative inline-flex items-center gap-2 text-sm font-bold text-[#1F3A5F] hover:text-[#E5276A]"
          data-testid="shop-cart-link">
          <ShoppingBag className="w-4 h-4" /> Panier
          {totals.count > 0 && (
            <span className="absolute -top-1.5 -right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E5276A] text-white text-[10px] font-bold flex items-center justify-center">
              {totals.count}
            </span>
          )}
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-8 md:gap-12">
        {/* Image */}
        <div className="bg-gradient-to-br from-[#FBF7F3] to-[#FCE4ED]/40 rounded-2xl overflow-hidden aspect-[4/5] sticky top-4">
          {(selected?.preview || product.image) ? (
            <img
              src={selected?.preview || product.image}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="shop-product-image"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#E5276A]/30">
              <Wine className="w-16 h-16" />
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <span className="chip-pink inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Carte geoVINUM
          </span>
          <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F] mt-3 leading-tight" data-testid="shop-product-name">
            {product.name}
          </h1>
          {selected && (
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-3xl text-[#E5276A]" data-testid="shop-product-price">
                {selected.price_eur.toFixed(2)} €
              </span>
              <span className="text-xs text-[#8B95A1]">TTC · livraison non incluse</span>
            </div>
          )}

          {/* Options selectors */}
          {Object.entries(grouped).map(([name, values]) => (
            <div key={name} className="mt-6">
              <div className="text-[11px] uppercase tracking-wider font-bold text-[#8B95A1] mb-2">{name}</div>
              <div className="flex flex-wrap gap-2">
                {values.map((v) => {
                  const active = selectedOpts[name] === v;
                  return (
                    <button
                      key={v}
                      data-testid={`shop-opt-${name.toLowerCase()}-${v.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      onClick={() => handleOptionChange(name, v)}
                      className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                        active
                          ? "bg-[#1F3A5F] text-white border-2 border-[#1F3A5F]"
                          : "bg-white text-[#1F3A5F] border-2 border-[#1F3A5F]/15 hover:border-[#E5276A]"
                      }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Quantity */}
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-wider font-bold text-[#8B95A1] mb-2">Quantité</div>
            <div className="inline-flex items-center gap-3 bg-white border-2 border-[#1F3A5F]/15 rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="shop-qty-minus"
                className="p-2.5 text-[#1F3A5F] hover:text-[#E5276A] rounded-l-full"><Minus className="w-4 h-4" /></button>
              <span className="text-lg font-bold text-[#1F3A5F] w-8 text-center" data-testid="shop-qty-val">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(10, q + 1))} data-testid="shop-qty-plus"
                className="p-2.5 text-[#1F3A5F] hover:text-[#E5276A] rounded-r-full"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={onAdd}
              disabled={!selected || adding}
              data-testid="shop-add-to-cart"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50 transition-colors"
            >
              {adding ? <Check className="w-5 h-5" /> : <ShoppingBag className="w-5 h-5" />}
              {adding ? "Ajouté !" : "Ajouter au panier"}
            </button>
            <button
              onClick={() => { onAdd(); setTimeout(() => nav("/shop/checkout"), 200); }}
              disabled={!selected}
              data-testid="shop-buy-now"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870] disabled:opacity-50 transition-colors"
            >
              Acheter maintenant
            </button>
          </div>

          {/* Trust */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-[#4A5568]">
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#E5276A]" /> Livraison mondiale</div>
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#E5276A]" /> Papier premium 200g</div>
            <div className="flex items-center gap-2"><Wine className="w-4 h-4 text-[#E5276A]" /> Conçu par sommeliers</div>
          </div>

          {/* Description */}
          {product.description && (
            <div className="mt-8 pt-6 border-t border-[#1F3A5F]/10">
              <h2 className="font-display text-lg text-[#1F3A5F] mb-3">Description</h2>
              <div
                className="prose prose-sm text-[#4A5568] leading-relaxed max-h-72 overflow-y-auto"
                data-testid="shop-product-description"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
